/**
 * Deep-freeze ricorsivo di un oggetto.
 *
 * Usato per disabilitare il walking reattivo di Vue 2 su collezioni
 * read-only post-bootstrap (drivers, proveniences, profiles, companies, pois).
 * Vedi `docs/shared/views/GeoFleet.md` sezione 9 (REFACTOR-008).
 *
 * Vue 2 osservatore: in `observe(value)` Vue controlla `Object.isExtensible(value)`
 * prima di creare un Observer. Un oggetto frozen è non-extensible → niente Observer →
 * niente getter/setter reattivi → niente walking dei devtools.
 *
 * Il freeze deve essere profondo perché Vue 2 walka ricorsivamente i sub-oggetti
 * quando incontra un riferimento a un oggetto non-frozen (es. `lastpoint.driver = drivers[id]`).
 *
 * REFACTOR-014 — reference cycles: la ricorsione usa un `WeakSet` (`seen`) per non
 * ripercorrere oggetti già visitati nella stessa discesa. Senza, un ciclo (es.
 * `a.b = a`) manderebbe in stack overflow. Le collezioni attuali (da `res.json()`)
 * non hanno cicli, ma la utility è generica: la guardia la rende sicura per chiamanti futuri.
 *
 * REFACTOR-015 — tipi speciali: `Date`/`Map`/`Set`/`RegExp` NON vengono congelati e
 * vengono restituiti così come sono. Congelarli è inutile o dannoso:
 *  - `Date`: diventerebbe immodificabile per i consumer (innocuo ma sgradito);
 *  - `Map`/`Set`: le mutate `.set()`/`.add()` falliscono silenti in strict mode;
 *  - `RegExp`: `lastIndex` immutabile romperebbe le regex globali.
 * Le collezioni attuali contengono solo plain object, ma il limite è ora esplicito e gestito.
 *
 * La firma pubblica resta `deepFreeze(obj)`: `seen` è un parametro interno con default.
 */
export function deepFreeze(obj, seen = new WeakSet()) {
    if (obj === null || typeof obj !== 'object') return obj

    // REFACTOR-015: tipi speciali → restituiti non congelati (vedi JSDoc)
    if (obj instanceof Date || obj instanceof Map || obj instanceof Set || obj instanceof RegExp) {
        return obj
    }

    // REFACTOR-014: già congelato, o già visitato in questa discesa (reference cycle)
    if (Object.isFrozen(obj) || seen.has(obj)) return obj

    seen.add(obj)

    Object.values(obj).forEach((v) => {
        if (v !== null && typeof v === 'object') deepFreeze(v, seen)
    })

    return Object.freeze(obj)
}
